import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ImageParamsWrapper, Movie } from '@usheru-hff/shared/domain';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { ImageService } from '@usheru-hff/shared/utils-image';

interface ImageResponsive {
  // breakpoint
  b: number | 'default';
  // width
  w: number;
}

/**
 * Component to render a picture element with different sizes of the images
 * as source to adapt to different screens sizes.
 *
 *
 * Examples:
 *
 * {@link widths}:  [800, 700, 500]
 *
 * {@link customWidths}:  [{b:'default', w: 500}, {b:700, w: 800}, {b:500, w: 700}]
 *
 * Reuslt:
 * default: show 500 | (min-width: 500) show 700 | (min-width: 700) show 800
 *
 *
 * **NOTES**: from highest to lowest for widths/customWidths. This will be sort but is better
 * have the right order. For customWidths can use 0 to replace 'default'.
 */
@Component({
  selector: 'ush-picture',
  templateUrl: './picture.component.html',
  styleUrls: ['./picture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PictureComponent implements OnInit {
  /**
   * If is a {@link Movie} get the type image to get from the {@link params} ({@link generateSrcset}).
   */
  @Input()
  source: string | Movie;

  /**
   * Used to ignore the {@link ImageService} in the channel.
   */
  @Input()
  isChannelPicture?: boolean;

  /**
   * @docNotNeccesary
   */
  @Input()
  alt: string;

  /**
   * Used to send on the {@link ImageService}.
   */
  @Input()
  params: ImageParamsWrapper;

  @Input()
  fixedWidth: number;

  /**
   * Array sizes to render the images.
   */
  @Input()
  widths: number[] = [];

  /**
   * Last posiotion on {@link widths}.
   */
  lastPosition: number;

  /**
   * Array sizes to render the images.
   *
   * Example:  [{b:'default', w: 500}, {b:700, w: 800}, {b:500, w: 700}]
   *
   *
   */
  @Input()
  customWidths: ImageResponsive[] = [
    { b: 0, w: 500 },
    { b: 500, w: 700 },
    { b: 700, w: 800 },
    { b: 1000, w: 1500 },
    { b: 1500, w: 1900 },
    { b: 2000, w: 2500 }
  ];

  /**
   * Case of the {@link customWidths} where the b (breakpoint) = 0.
   */
  customDefaultWidth: number;

  /**
   * Clss name to add to the picture element.
   *
   * //TODO add this class to "no image template"
   */
  @Input()
  customClass: string;

  /**
   * Indicates if use the {@link ModalPictureService}.
   */
  @Input()
  modal: boolean;

  /**
   * Force to use the {@link placeholder}.
   */
  @Input()
  usePlaceholder = false;

  /**
   * @docNotNeccesary
   */
  @Input()
  placeholderType: 'portrait' | 'tile' = 'tile';

  /**
   * Image to show if there is some type of error with the {@link source}.
   */
  placeholderImage: string;

  /**
   * Result of apply the {@link ImageService} to the {@link placeholderImage}
   */
  placeholderImageEncrypt: string;

  /**
   * Renders a div to replace the image.
   */
  noImage = false;

  /**
   * {@link noImage} height to match some aspect ratios.
   */
  noImageHeight: number;

  /**
   * @docNotNeccesary
   */
  channelName: string;

  /**
   * Indicates if some errors happened with the {@link source}. Used to no repeat the {@link handleError}.
   */
  hasErrors = false;

  constructor(
    private channelService: ChannelService,
    private cd: ChangeDetectorRef,
    private imageService: ImageService
  ) {
    this.channelName = this.channelService.channel.name;
  }

  /**
   * * Prepare placeholder.
   *
   * * Check if the {@link widths} is actually a {@link customWidths} parse it.
   *
   * * If {@link customWidths} in present, fix default values.
   */
  ngOnInit() {
    // prepare placeholder
    const portraitPlaceholder = this.channelService.channel.portraitPhotoUrl;
    const tilePlaceholder = this.channelService.channel.tilePhotoUrl;
    if (this.params?.type) {
      if (this.params.type === 'landscape') this.params.type = 'tile';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.placeholderType = this.params.type as any;
    }

    if (this.placeholderType === 'portrait' && portraitPlaceholder) {
      this.placeholderImage = portraitPlaceholder;
    } else if (this.placeholderType == 'tile' && tilePlaceholder) {
      this.placeholderImage = tilePlaceholder;
    } else if (this.params?.type) {
      this.placeholderImage = this.params.type;
      if (this.placeholderImage === 'landscape') this.placeholderImage = 'tile';
      if (this.placeholderImage !== 'landscape' && this.placeholderImage === 'portrait') this.placeholderImage = 'tile';
    }

    if (this.usePlaceholder || this.source == null) {
      this.source = this.placeholderImage;
      this.widths = this.widths.length > 0 ? this.widths : [200];
      this.params = { type: this.placeholderType };
      if (this.placeholderType === 'portrait') {
        this.params.height = 300;
      }
    }

    // check if the widths is actually a customWidths to set this value
    if (this.widths?.length > 0 && typeof this.widths[0] == 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.customWidths = this.widths as any;
    }

    if (this.widths?.length > 0) {
      this.lastPosition = this.widths.length - 1;
      this.widths.sort((a, b) => b - a);
    }
    if (this.customWidths?.length > 0) {
      // fix default
      const zerobreakpoint = this.customWidths.find(cw => cw.b === 0);
      if (zerobreakpoint) zerobreakpoint.b = 'default';
      this.customDefaultWidth = this.customWidths.find(cw => cw.b === 'default').w;
      this.customWidths.sort((a, b) => {
        if (a.b === 'default') a.b = 0;
        if (b.b === 'default') b.b = 0;
        return b.b - a.b;
      });
    }
  }

  /**
   * Used in the "media" attr in the source element when {@link widths} in used.
   * @param width
   * @returns
   */
  generateMinWidth(width: number) {
    // if (false && this.isChannelPicture) {
    //   return `(min-width:100%)`;
    // }
    return `(min-width:${width}px)`;
  }

  /**
   * Selects and parse the image with the {@link ImageService} computeUrl.
   * @param width
   * @returns
   */
  generateSrcset(width: number) {
    // if (false && this.isChannelPicture) {
    //   if (typeof this.source === 'string') {
    //     return this.source;
    //   } else {
    //     return this.source.tilePhotoUrl;
    //   }
    // }
    let sourceUrl;
    // if source is movie check if wether we should use landscape or portrait image
    if (typeof this.source === 'string') {
      sourceUrl = this.source;
    } else {
      if (this.params.type == 'landscape' || this.params.type == 'tile') {
        sourceUrl = this.source.tilePhotoUrl;
      } else {
        sourceUrl = this.source.portraitPhotoUrl;
      }
    }
    const srcset = this.imageService.computeUrl(sourceUrl, { ...this.params, width });
    return srcset;
  }

  /**
   * Used only in the img element of picture. Not in the source elements.
   * @returns
   */
  generateSrcsetDefaultCustomWidths() {
    if (false && this.isChannelPicture) {
      if (typeof this.source === 'string') {
        return this.imageService.computeUrl(this.source as string, { ...this.params, width: 300 });
      } else {
        return this.imageService.computeUrl((this.source as Movie).tilePhotoUrl, { ...this.params, width: 300 });
      }
    }
    let sourceUrl;
    let defaultWidth;
    // if source is movie check if wether we should use landscape or portrait image
    if (typeof this.source === 'string') {
      sourceUrl = this.source;
    } else {
      if (this.params.type == 'landscape' || this.params.type == 'tile') {
        sourceUrl = this.source?.tilePhotoUrl;
        defaultWidth = 450;
      } else {
        sourceUrl = this.source?.portraitPhotoUrl;
        defaultWidth = 360;
      }
    }
    return this.imageService.computeUrl(sourceUrl, { ...this.params, width: this.customDefaultWidth || defaultWidth });
  }

  /**
   * Response when somethin wrong happend loading the {@link source}.
   * @param event
   * @returns
   */
  handleError(/*event*/) {
    if (this.hasErrors) return;
    if (this.placeholderImage) {
      this.placeholderImageEncrypt = this.imageService.computeUrl(this.placeholderImage, { height: 300 });
    } else {
      switch (this.placeholderType) {
        case 'tile':
          this.noImageHeight = ((this.customDefaultWidth || this.widths[this.lastPosition]) * 9) / 16;
          break;
        case 'portrait':
          this.noImageHeight = ((this.customDefaultWidth || this.widths[this.lastPosition]) * 4) / 3;
          break;
      }
      this.noImage = true;
    }

    this.hasErrors = true;
  }

  /**
   * @docNotNeccesary
   */
  openModal() {
    // const fileName = typeof this.source !== 'string' ? this.source.trTitle : null;
  }
}
